import {
    AcademicProgramAPIModel,
    AcademicYearAPIModel,
    AssessmentCycleAPIModel,
    CreateAcademicProgramRequest,
    CreateAcademicYearRequest,
    CreateAssessmentCycleRequest,
    CreateQualificationModuleRequest,
    CreateQualificationProgramLevelRequest,
    CreateTeachingGroupRequest,
    ICreateAcademicProgramRequest,
    ICreateAcademicYearRequest,
    ICreateAssessmentCycleRequest,
    ICreateQualificationModuleRequest,
    ICreateQualificationProgramLevelRequest,
    ICreateTeachingGroupRequest,
    IUpdateAcademicProgramRequest,
    IUpdateAcademicYearRequest,
    IUpdateAssessmentCycleRequest,
    IUpdateQualificationModuleRequest,
    IUpdateQualificationProgramLevelRequest,
    IUpdateTeachingGroupRequest,
    QualificationModuleAPIModel,
    QualificationProgramLevelAPIModel,
    TeachingGroupAPIModel,
    UpdateAcademicProgramRequest,
    UpdateAcademicYearRequest,
    UpdateAssessmentCycleRequest,
    UpdateQualificationModuleRequest,
    UpdateQualificationProgramLevelRequest,
    UpdateTeachingGroupRequest,
} from "../api/APIClient";
import { managementClient } from "../api/clients";
import { unwrap, unwrapVoid } from "../api/errors";

/**
 * The time-bound offering: which parts of the catalogue actually run, when, and for whom.
 *
 * The pivot of the entire domain is `QualificationModule` - one subject, for one cohort, in one
 * cycle. It is what an assessment tree hangs off, what a procedure assignment binds to, and what the
 * calculation engine targets. Everything else here exists to define one.
 *
 * `AssessmentCycle.Rank` is how terms and semesters are both modelled. Legacy data is 0-based with
 * -1 meaning "full year"; this system is 1-based. Do not mix the two.
 *
 * Same envelope discipline as the Structure store: HTTP 200 with `success: false` is a failure, so
 * every call goes through `unwrap`/`unwrapVoid`.
 */

export type {
    AcademicProgramAPIModel,
    AcademicYearAPIModel,
    AssessmentCycleAPIModel,
    QualificationModuleAPIModel,
    QualificationProgramLevelAPIModel,
    TeachingGroupAPIModel,
};

// -------------------------------------------------------------- academic years

export const listAcademicYears = (): Promise<AcademicYearAPIModel[]> =>
    managementClient.listAcademicYears().then((r) => unwrap(r) ?? []);

export const createAcademicYear = (model: ICreateAcademicYearRequest): Promise<void> =>
    managementClient.createAcademicYear(new CreateAcademicYearRequest(model)).then(unwrapVoid);

export const updateAcademicYear = (model: IUpdateAcademicYearRequest): Promise<void> =>
    managementClient.updateAcademicYear(new UpdateAcademicYearRequest(model)).then(unwrapVoid);

export const deleteAcademicYear = (id: string): Promise<void> => managementClient.deleteAcademicYear(id).then(unwrapVoid);

// ----------------------------------------------------------- assessment cycles

export const listAssessmentCycles = (): Promise<AssessmentCycleAPIModel[]> =>
    managementClient.listAssessmentCycles().then((r) => unwrap(r) ?? []);

export const createAssessmentCycle = (model: ICreateAssessmentCycleRequest): Promise<void> =>
    managementClient.createAssessmentCycle(new CreateAssessmentCycleRequest(model)).then(unwrapVoid);

export const updateAssessmentCycle = (model: IUpdateAssessmentCycleRequest): Promise<void> =>
    managementClient.updateAssessmentCycle(new UpdateAssessmentCycleRequest(model)).then(unwrapVoid);

export const deleteAssessmentCycle = (id: string): Promise<void> => managementClient.deleteAssessmentCycle(id).then(unwrapVoid);

// ----------------------------------------------------------- academic programs

export const listAcademicPrograms = (): Promise<AcademicProgramAPIModel[]> =>
    managementClient.listAcademicPrograms().then((r) => unwrap(r) ?? []);

export const createAcademicProgram = (model: ICreateAcademicProgramRequest): Promise<void> =>
    managementClient.createAcademicProgram(new CreateAcademicProgramRequest(model)).then(unwrapVoid);

export const updateAcademicProgram = (model: IUpdateAcademicProgramRequest): Promise<void> =>
    managementClient.updateAcademicProgram(new UpdateAcademicProgramRequest(model)).then(unwrapVoid);

export const deleteAcademicProgram = (id: string): Promise<void> => managementClient.deleteAcademicProgram(id).then(unwrapVoid);

// ------------------------------------------------- qualification program levels

export const listQualificationProgramLevels = (): Promise<QualificationProgramLevelAPIModel[]> =>
    managementClient.listQualificationProgramLevels().then((r) => unwrap(r) ?? []);

export const createQualificationProgramLevel = (model: ICreateQualificationProgramLevelRequest): Promise<void> =>
    managementClient.createQualificationProgramLevel(new CreateQualificationProgramLevelRequest(model)).then(unwrapVoid);

export const updateQualificationProgramLevel = (model: IUpdateQualificationProgramLevelRequest): Promise<void> =>
    managementClient.updateQualificationProgramLevel(new UpdateQualificationProgramLevelRequest(model)).then(unwrapVoid);

export const deleteQualificationProgramLevel = (id: string): Promise<void> =>
    managementClient.deleteQualificationProgramLevel(id).then(unwrapVoid);

// ---------------------------------------------------------- qualification modules

export const listQualificationModules = (): Promise<QualificationModuleAPIModel[]> =>
    managementClient.listQualificationModules().then((r) => unwrap(r) ?? []);

export const createQualificationModule = (model: ICreateQualificationModuleRequest): Promise<void> =>
    managementClient.createQualificationModule(new CreateQualificationModuleRequest(model)).then(unwrapVoid);

export const updateQualificationModule = (model: IUpdateQualificationModuleRequest): Promise<void> =>
    managementClient.updateQualificationModule(new UpdateQualificationModuleRequest(model)).then(unwrapVoid);

export const deleteQualificationModule = (id: string): Promise<void> =>
    managementClient.deleteQualificationModule(id).then(unwrapVoid);

// --------------------------------------------------------------- teaching groups

/**
 * The class or register group. A roster filter, NOT a calculation target - do not confuse it with
 * `AssessmentGroup`, which is the tree the engine calculates against.
 */
export const listTeachingGroups = (): Promise<TeachingGroupAPIModel[]> =>
    managementClient.listTeachingGroups().then((r) => unwrap(r) ?? []);

export const createTeachingGroup = (model: ICreateTeachingGroupRequest): Promise<void> =>
    managementClient.createTeachingGroup(new CreateTeachingGroupRequest(model)).then(unwrapVoid);

export const updateTeachingGroup = (model: IUpdateTeachingGroupRequest): Promise<void> =>
    managementClient.updateTeachingGroup(new UpdateTeachingGroupRequest(model)).then(unwrapVoid);

export const deleteTeachingGroup = (id: string): Promise<void> => managementClient.deleteTeachingGroup(id).then(unwrapVoid);

// --------------------------------------------------------------------- helpers

/**
 * A qualification module has no name of its own - it is a join of program level, module and cycle.
 * Everywhere one has to be picked (procedure assignment, mark entry, the structure editor) it needs
 * the same composed label, so it is built once here.
 */
export function qualificationModuleLabel(qm?: QualificationModuleAPIModel): string {
    if (!qm) return "";
    const module = qm.module?.name ?? "(module)";
    const level = qm.qualificationProgramLevel?.qualificationLevel?.name;
    const qualification = qm.qualificationProgramLevel?.qualification?.name;
    const cycle = qm.assessmentCycle?.name;
    const year = qm.qualificationProgramLevel?.academicYear?.name;
    const program = qm.qualificationProgramLevel?.academicProgram?.name;

    return [module, [qualification, level].filter(Boolean).join(" "), year, cycle, program].filter(Boolean).join(" - ");
}

export function programLevelLabel(level?: QualificationProgramLevelAPIModel): string {
    if (!level) return "";
    return (
        [level.qualification?.name, level.qualificationLevel?.name, level.academicYear?.name].filter(Boolean).join(" - ") ||
        (level.id ?? "")
    );
}

/** `<input type="date">` wants yyyy-mm-dd; the API sends and expects a Date. */
export const toDateInput = (value?: Date): string => (value ? new Date(value).toISOString().slice(0, 10) : "");

export const fromDateInput = (value: string): Date | undefined => (value ? new Date(value) : undefined);
